import { useRef, useState } from 'react'

import { ChevronDown } from 'lucide-react'
import styled, { css } from 'styled-components'

import mastercardLogo from 'assets/images/mastercard.png'
import visaLogo from 'assets/images/visa.png'
import * as spriteIcons from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useUserProfile } from 'store'


interface DropdownCard {
  id: string
  card_number: string
  is_expired: boolean
  expiration_month: number | string
  expiration_year: number | string
}

interface DropdownCardSelectorProps {
  cardList: { cards: DropdownCard[] }
  paymentMethod: {
    method: 'card' | 'deposit'
    cardId: string | undefined
    rebill?: boolean
  }
  setPaymentMethod: (method: {
    method: 'card' | 'deposit'
    cardId: string | undefined
    rebill?: boolean
  }) => void

  walletCanCover?: boolean
  walletTotalDue?: number
  walletRequired?: number
  
}
type PaymentOption = {
  id?: string
  method: 'card' | 'deposit'
  label: string
  icon: JSX.Element
  subtext?: string | JSX.Element
  description?: string
  disabled?: boolean
}

export function DropdownCardSelector({
  cardList,
  paymentMethod,
  setPaymentMethod,
  walletCanCover,
  walletRequired
}: DropdownCardSelectorProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const userData = useUserProfile()
  const walletBalance = Number(userData?.wallet_deposit ?? 0)

  function formatAmount(amount: number) {
    return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)
  }

  const showInsufficient =
    paymentMethod.method === 'deposit' && walletCanCover === false

  const allOptions: PaymentOption[] = [
    ...(walletBalance >= 0
      ? [
          {
            method: 'deposit' as const,
            label: 'Wallet',
            icon: <spriteIcons.IconWallet />,
            subtext: (
              <WalletSubtext>
                <div>
                  <span style={{ color: '#7d889b' }}>Balance </span>
                  <span style={{ color: '#202331', fontWeight: 600 }}>
                    ${formatAmount(walletBalance)}
                  </span>
                </div>

                {showInsufficient && (
                  <InsufficientFunds>
                    Insufficient Funds
                  </InsufficientFunds>
                )}
              </WalletSubtext>
            )
          }
        ]
      : []),
    ...(cardList?.cards ?? []).map(card => {
      const isVisa = card.card_number.charAt(0) === '3'

      return {
        id: card.id,
        method: 'card' as const,
        label: isVisa ? 'Visa' : 'Mastercard',
        icon: (
          <img
            src={isVisa ? visaLogo : mastercardLogo}
            alt={isVisa ? 'Visa' : 'Mastercard'}
            width={40}
          />
        ),
        subtext: card.card_number,
        description: card.is_expired
          ? `Expired ${card.expiration_month}/${card.expiration_year}`
          : `Exp ${card.expiration_month}/${card.expiration_year}`,
        disabled: card.is_expired
      }
    })
  ]

  const selectedFromState = allOptions.find(opt => {
    if (opt.method !== paymentMethod.method) {
      return false
    }

    if (opt.method === 'deposit') {
      return true
    }

    return String(opt.id) === String(paymentMethod.cardId)
  })

  const firstValidCard = allOptions.find(
    opt =>
      opt.method === 'card' &&
      !opt.disabled
  )

  const walletOption = allOptions.find(
    opt => opt.method === 'deposit'
  )

  /*
  * Never allow the selector to be visually empty.
  *
  * Priority:
  * 1. Actual selected option
  * 2. First valid card
  * 3. Wallet
  * 4. Any remaining option
  */
  const selected =
    selectedFromState ??
    firstValidCard ??
    walletOption ??
    allOptions[0]

  const otherOptions = allOptions.filter(opt => {
    if (!selected) {
      return true
    }

    if (opt.method !== selected.method) {
      return true
    }

    if (selected.method === 'deposit') {
      return false
    }

    return String(opt.id) !== String(selected.id)
  })

  const handleSelect = (opt: PaymentOption) => {
    if (opt.disabled) return

    setPaymentMethod({
      method: opt.method,
      cardId: opt.id ?? undefined
    })

    setOpen(false)
  }

  const hasOtherOptions = otherOptions.length > 0

  return (
    <DropdownContainer ref={ref}>
      <DropdownHeader
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => hasOtherOptions && setOpen(o => !o)}
        style={{ cursor: hasOtherOptions ? 'pointer' : 'default' }}
      >
        {selected && (
          <ActionCard
            icon={selected.icon}
            text={<>{selected.label}</>}
            subtext={selected.subtext && <span>{selected.subtext}</span>}
            description={selected.description && <span>{selected.description}</span>}
            iconSize="small"
            selected
            clickFn={() => {}}
            customStyle={css`
              padding: 1rem 1.5rem;
              margin-top: 1rem;
              min-height: 3.5rem;
            `}
          />
        )}

        {hasOtherOptions && (
          <ChevronDown
            size={16}
            style={{
              color: '#666',
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
        )}
      </DropdownHeader>

      {open && hasOtherOptions && (
        <DropdownList>
          {otherOptions.map(opt => (
            <OptionItem
              key={`${opt.method}-${opt.id ?? 'wallet'}`}
              style={{
                opacity: opt.disabled ? 0.5 : 1,
                cursor: opt.disabled ? 'not-allowed' : 'pointer'
              }}
            >
              <ActionCard
                icon={opt.icon}
                text={<>{opt.label}</>}
                subtext={opt.subtext && <span>{opt.subtext}</span>}
                description={opt.description && <span>{opt.description}</span>}
                iconSize="small"
                selected={false}
                clickFn={() => handleSelect(opt)}
                customStyle={css`
                  padding: 1rem 1.5rem;
                  min-height: 4.5rem;
                  border: 1px solid #e0e0e0;
                  box-shadow: none !important;
                `}
              />
            </OptionItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  )
}

// Styled Components
const DropdownContainer = styled.div`
  width: 100%;
  position: relative;
  padding-right: 0.4rem;
  padding-left: 0.4rem;
  overflow-x:hidden;
`

const DropdownHeader = styled.div`
  position: relative;
  cursor: pointer;

  // Prevent blue outline on focus for webkit browsers
  &:focus {
    outline: none;
  }

  *:focus {
    outline: none;
  }

  // Specific to WebKit (Chrome, Safari)
  &::-moz-focus-inner {
    border: 0;
  }

  &::-webkit-focus-ring-color {
    outline: none;
  }

  // Remove highlight color in Safari/iOS when tapping
  -webkit-tap-highlight-color: transparent;
`

const Arrow = styled.span`
  position: absolute;
  top: 24px;
  right: 16px;
  font-size: 1.2rem;
  color: #999;
  pointer-events: none;
`

const DropdownList = styled.div`
  margin-top: 8px;
  border-radius: 16px;
  background: white;
  overflow: hidden;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`

const OptionItem = styled.div`
  border-top: 1px solid #f0f0f0;
`
const WalletSubtext = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InsufficientFunds = styled.div`
  color: #e53935;
  font-size: 12px;
  font-weight: 500;
`